import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Ticket, Clock, Send, Mail } from 'lucide-react';
import { commerceAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Field, inputCls, Toggle, ConfirmDialog, fmtDate, toInputDate, rewardLabel } from './shared';
import { Textarea } from '@/components/ui/textarea';

const EMPTY = { name: '', description: '', kind: 'percent', value: 10, applies_to: [], starts_at: '', ends_at: '', code: '', max_uses: '', per_employer_limit: '', active: true };

const PromoForm = ({ open, initial, plans, onClose, onSaved, t }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial, description: initial.description || '', code: initial.code || '', starts_at: toInputDate(initial.starts_at), ends_at: toInputDate(initial.ends_at), max_uses: initial.max_uses ?? '', per_employer_limit: initial.per_employer_limit ?? '' } : EMPTY);
  }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const togglePlan = (pt) => setF({ ...f, applies_to: f.applies_to.includes(pt) ? f.applies_to.filter((x) => x !== pt) : [...f.applies_to, pt] });
  const save = async () => {
    setSaving(true);
    const body = {
      name: f.name, description: f.description || null, kind: f.kind, value: Number(f.value), applies_to: f.applies_to,
      starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : null, ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null,
      code: f.code || null, max_uses: f.max_uses === '' ? (isEdit ? 0 : null) : Number(f.max_uses),
      per_employer_limit: f.per_employer_limit === '' ? (isEdit ? 0 : null) : Number(f.per_employer_limit), active: f.active,
    };
    try {
      if (isEdit) await commerceAPI.updatePromotion(initial.id, { ...body, clear_dates: !f.starts_at && !f.ends_at });
      else await commerceAPI.createPromotion(body);
      toast.success(t('adminCommerce.saved'));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t('adminCommerce.saveError')); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promo-form">
        <DialogHeader><DialogTitle>{isEdit ? t('adminCommerce.promos.edit') : t('adminCommerce.promos.new')}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t('adminCommerce.promos.name')} className="sm:col-span-2"><input className={inputCls} value={f.name} onChange={set('name')} placeholder="Semana do Emprego" data-testid="promo-name" /></Field>
          <Field label={t('adminCommerce.kind')}>
            <select className={inputCls} value={f.kind} onChange={set('kind')} data-testid="promo-kind">
              <option value="percent">{t('adminCommerce.kinds.percent')}</option>
              <option value="fixed">{t('adminCommerce.kinds.fixed')}</option>
              <option value="bonus_credits">{t('adminCommerce.kinds.bonus_credits')}</option>
            </select>
          </Field>
          <Field label={t('adminCommerce.value')} hint={t(`adminCommerce.valueHint.${f.kind}`)}><input type="number" min="1" className={inputCls} value={f.value} onChange={set('value')} data-testid="promo-value" /></Field>
          <Field label={t('adminCommerce.promos.code')} hint={t('adminCommerce.promos.codeHint')}><input className={`${inputCls} font-mono uppercase`} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="VERAO25" data-testid="promo-code" /></Field>
          <Field label={t('adminCommerce.promos.maxUses')} hint={t('adminCommerce.promos.unlimitedHint')}><input type="number" min="0" className={inputCls} value={f.max_uses} onChange={set('max_uses')} data-testid="promo-max-uses" /></Field>
          <Field label={t('adminCommerce.promos.startsAt')}><input type="datetime-local" className={inputCls} value={f.starts_at} onChange={set('starts_at')} data-testid="promo-starts" /></Field>
          <Field label={t('adminCommerce.promos.endsAt')} hint={t('adminCommerce.promos.endsHint')}><input type="datetime-local" className={inputCls} value={f.ends_at} onChange={set('ends_at')} data-testid="promo-ends" /></Field>
          <Field label={t('adminCommerce.promos.perEmployer')} hint={t('adminCommerce.promos.unlimitedHint')}><input type="number" min="0" className={inputCls} value={f.per_employer_limit} onChange={set('per_employer_limit')} /></Field>
          <div className="flex items-end"><Toggle checked={f.active} onChange={(v) => setF({ ...f, active: v })} label={f.active ? t('adminCommerce.active') : t('adminCommerce.inactive')} testId="promo-active-toggle" /></div>
          <div className="sm:col-span-2">
            <p className="text-sm font-medium text-slate-700 mb-2">{t('adminCommerce.promos.appliesTo')} <span className="text-xs text-slate-500 font-normal">({t('adminCommerce.promos.appliesAllHint')})</span></p>
            <div className="flex flex-wrap gap-2">
              {plans.filter((p) => p.price_cve > 0).map((p) => (
                <Toggle key={p.plan_type} checked={f.applies_to.includes(p.plan_type)} onChange={() => togglePlan(p.plan_type)} label={p.name_pt} testId={`promo-plan-${p.plan_type}`} />
              ))}
            </div>
          </div>
          <Field label={t('adminCommerce.description')} className="sm:col-span-2"><textarea className={`${inputCls} h-16`} value={f.description} onChange={set('description')} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('adminCommerce.cancel')}</Button>
          <Button onClick={save} disabled={saving || !f.name || !f.value} className="bg-emerald-600 hover:bg-emerald-700" data-testid="promo-save-btn">{t('adminCommerce.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AnnounceDialog = ({ promo, onClose, onSent, t }) => {
  const [intro, setIntro] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await commerceAPI.announcePromotion(promo.id, intro);
      toast.success(t('adminCommerce.promos.announceDone', { count: data.queued }));
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || t('adminCommerce.saveError')); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={Boolean(promo)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="announce-dialog">
        <DialogHeader><DialogTitle>{t('adminCommerce.promos.announceTitle')}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{t('adminCommerce.promos.announceBody', { name: promo?.name })}</p>
        {promo?.announced_at && <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">{t('adminCommerce.promos.announcedBefore', { date: fmtDate(promo.announced_at), count: promo.announced_count })}</p>}
        <Field label={t('adminCommerce.promos.announceIntro')} hint={t('adminCommerce.promos.announceIntroHint')}>
          <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} maxLength={600} className="mt-1" data-testid="announce-intro" />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('adminCommerce.cancel')}</Button>
          <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="announce-send-btn"><Send className="h-4 w-4 mr-1" />{sending ? '…' : t('adminCommerce.promos.announceSend')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PromoRow = ({ p, plans, onEdit, onToggle, onDelete, onAnnounce, t }) => {
  const names = (p.applies_to || []).map((pt) => plans.find((x) => x.plan_type === pt)?.name_pt || pt);
  return (
    <Card className={`p-4 flex flex-col md:flex-row md:items-center gap-4 ${p.active ? '' : 'opacity-60'}`} data-testid={`promo-row-${p.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-slate-900">{p.name}</h3>
          <span className="text-sm font-bold text-emerald-700">{rewardLabel(p.kind, p.value, t)}</span>
          {p.code
            ? <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2 py-0.5 rounded bg-slate-900 text-white"><Ticket className="h-3 w-3" />{p.code}</span>
            : <span className="text-[11px] font-bold uppercase px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{t('adminCommerce.promos.automatic')}</span>}
          <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${p.is_live ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`} data-testid={`promo-live-${p.id}`}>
            {p.is_live ? t('adminCommerce.promos.live') : t('adminCommerce.promos.notLive')}
          </span>
        </div>
        <p className="text-sm text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-1">
          <span><Clock className="inline h-3.5 w-3.5 mr-1" />{fmtDate(p.starts_at)} → {p.ends_at ? fmtDate(p.ends_at) : t('adminCommerce.promos.noEnd')}</span>
          <span>{t('adminCommerce.promos.uses', { uses: p.uses || 0, max: p.max_uses ?? '∞' })}</span>
          <span>{names.length ? names.join(', ') : t('adminCommerce.promos.allPlans')}</span>
          {p.announced_at && <span className="text-emerald-700" data-testid={`promo-announced-${p.id}`}><Mail className="inline h-3.5 w-3.5 mr-1" />{t('adminCommerce.promos.announcedOn', { date: fmtDate(p.announced_at), count: p.announced_count })}</span>}
          {p.influencer_id && <span className="text-fuchsia-700 font-medium">{t('adminCommerce.promos.influencerCode')}</span>}
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {p.is_live && (
          <Button size="sm" onClick={() => onAnnounce(p)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`promo-announce-${p.id}`}><Send className="h-4 w-4 mr-1" />{t('adminCommerce.promos.announce')}</Button>
        )}
        <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`promo-edit-${p.id}`}><Pencil className="h-4 w-4 mr-1" />{t('adminCommerce.edit')}</Button>
        <Button size="sm" variant="outline" onClick={() => onToggle(p)} data-testid={`promo-toggle-${p.id}`}>{p.active ? t('adminCommerce.deactivate') : t('adminCommerce.activate')}</Button>
        <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => onDelete(p)} data-testid={`promo-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </Card>
  );
};

export const PromotionsTab = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toAnnounce, setToAnnounce] = useState(null);
  const load = () => Promise.all([commerceAPI.listPromotions(), commerceAPI.listPlans()])
    .then(([a, b]) => { setItems(a.data); setPlans(b.data); }).catch(() => toast.error(t('adminCommerce.loadError')));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (p) => { await commerceAPI.updatePromotion(p.id, { active: !p.active }); load(); };
  const del = async () => { await commerceAPI.deletePromotion(toDelete.id); setToDelete(null); toast.success(t('adminCommerce.promos.deleted')); load(); };
  return (
    <div data-testid="promotions-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-slate-600 max-w-xl">{t('adminCommerce.promos.intro')}</p>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="promo-new-btn"><Plus className="h-4 w-4 mr-1" />{t('adminCommerce.promos.new')}</Button>
      </div>
      {items.length === 0 && <Card className="p-8 text-center text-slate-500" data-testid="promos-empty">{t('adminCommerce.promos.empty')}</Card>}
      <div className="space-y-3">
        {items.map((p) => <PromoRow key={p.id} p={p} plans={plans} t={t} onEdit={(x) => { setEditing(x); setFormOpen(true); }} onToggle={toggle} onDelete={setToDelete} onAnnounce={setToAnnounce} />)}
      </div>
      <AnnounceDialog promo={toAnnounce} onClose={() => setToAnnounce(null)} onSent={() => { setToAnnounce(null); load(); }} t={t} />
      <PromoForm open={formOpen} initial={editing} plans={plans} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} t={t} />
      <ConfirmDialog open={Boolean(toDelete)} title={t('adminCommerce.promos.deleteTitle')} body={t('adminCommerce.promos.deleteBody', { name: toDelete?.name })} onCancel={() => setToDelete(null)} onConfirm={del} confirmLabel={t('adminCommerce.delete')} cancelLabel={t('adminCommerce.cancel')} testId="promo-delete-dialog" />
    </div>
  );
};
